import { useState } from 'react';
import { ewsDashboardAPI } from '../../services/api';
import { Modal } from './DashboardPrimitives';
import { EWS_COLUMN_HINT, EWS_COLUMN_LABEL, type EwsColumn } from './ews';

/**
 * Dashboard → EWS: which parameters the project monitors, for its admins.
 *
 * For the whole project: a parameter switched off is not shown to anyone and adds no
 * warning sign, so Red, Amber and the contact timers change with it. Only the parameters
 * the project has data for are offered.
 */
export default function EwsParametersModal({ projectId, choices, onSaved, onClose }: {
  projectId: number;
  choices: Array<{ key: EwsColumn; on: boolean }>;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [on, setOn] = useState<Set<EwsColumn>>(() => new Set(choices.filter((c) => c.on).map((c) => c.key)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (key: EwsColumn) => setOn((cur) => {
    const next = new Set(cur);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const save = () => {
    setSaving(true);
    setError(null);
    ewsDashboardAPI.setColumns(projectId, { off: choices.filter((c) => !on.has(c.key)).map((c) => c.key) })
      .then(() => onSaved())
      .catch((err) => setError(err?.response?.data?.message || 'The parameters could not be saved. Try again in a minute.'))
      .finally(() => setSaving(false));
  };

  return (
    <Modal title="Parameters" onClose={onClose} maxWidth="max-w-lg">
      <p className="text-sm text-gray-500">For everyone in this project. A parameter switched off is not shown and adds no warning sign.</p>
      <ul className="mt-3 divide-y divide-gray-100">
        {choices.map((c) => (
          <li key={c.key}>
            <label className="flex cursor-pointer items-start gap-3 py-2.5">
              <input
                type="checkbox"
                checked={on.has(c.key)}
                onChange={() => toggle(c.key)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">{EWS_COLUMN_LABEL[c.key]}</span>
                <span className="block text-xs text-gray-500">{EWS_COLUMN_HINT[c.key]}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      {error && <p className="mt-2 text-sm text-rose-700">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}
